/** Marker pins, ported from TourDetailsScreenStateMapper.mapPin / mapSlotIcon.
 *
 * Upstream composes a pin from a base drawable (`ic_uikit_pin_base`, or
 * `ic_uikit_pin_base_selected`) with a 20dp icon laid over it at anchor (0.5, 0.35),
 * and anchors the whole marker at (0.5, 0.95). The icon is one of four things:
 *
 *   Numbered        text `slotDisplayIndex + 1`
 *   NumberedCustom  text `pin_custom_number`
 *   Icon(key)       a bundled drawable chosen from a 12-key table, tinted
 *   IconCustom(url) the operator's own image at that URL, tinted
 *
 * NOTE on fidelity: the base teardrop and the twelve `ic_uikit_pin_*` glyphs are
 * Android vector drawables that have NOT been exported into this repo, so the shape
 * below is a hand-drawn equivalent and `Icon(key)` falls back to a labelled dot. Both
 * are deliberate and visible rather than silently approximate — exporting the real
 * assets is a separate change, and it needs `provenance.json` entries so the drift
 * check covers them the way it covers the colour tokens.
 *
 * Upstream: ui/my_trips_details/details/TourDetailsScreenStateMapper.kt:561-655
 */
import type { SlotPin } from '../adapters/tour';

/** Marker anchor — the point of the teardrop sits on the coordinate. */
export const MARKER_ANCHOR = { x: 0.5, y: 0.95 } as const;

/** `getIconRes()` — the twelve keys, lowercased, with `meeting_point` as the default
 *  for anything unrecognised. Kept as the real list so an operator typing a key that
 *  is NOT in it can see the fallback happen, which is the editorial point. */
export const PIN_ICON_KEYS = [
  'accommodation',
  'activity',
  'bars',
  'cafes',
  'culture',
  'events',
  'meeting_point',
  'nature',
  'restaurants',
  'shopping',
  'sightseeing',
  'transportation',
] as const;

export type PinIconKey = (typeof PIN_ICON_KEYS)[number];

export function resolveIconKey(key: string): PinIconKey {
  const k = key.toLowerCase();
  return (PIN_ICON_KEYS as readonly string[]).includes(k) ? (k as PinIconKey) : 'meeting_point';
}

/** What the pin should draw, once the slot's pin shape and its display index are known. */
export type PinFace =
  | { kind: 'text'; text: string }
  | { kind: 'icon'; key: PinIconKey; recognised: boolean }
  | { kind: 'image'; url: string };

/** `mapSlotIcon` — note Numbered renders `slotDisplayIndex + 1`, i.e. the SAME number
 *  the list rail shows, which is why the two can never disagree. */
export function pinFace(pin: SlotPin, slotDisplayIndex: number): PinFace {
  switch (pin.kind) {
    case 'numbered':
      return { kind: 'text', text: String(slotDisplayIndex + 1) };
    case 'numberedCustom':
      return { kind: 'text', text: String(pin.value) };
    case 'icon':
      return {
        kind: 'icon',
        key: resolveIconKey(pin.key),
        recognised: (PIN_ICON_KEYS as readonly string[]).includes(pin.key.toLowerCase()),
      };
    case 'iconCustom':
      return { kind: 'image', url: pin.url };
  }
}
